"use client";

import { c as _c } from "react/compiler-runtime";
import { forwardRef, useCallback } from "react";
import { Button, type buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { prefersReducedMotion } from "@/lib/reduced-motion";
import type { VariantProps } from "class-variance-authority";

/**
 * ExpressiveButton — envuelve <Button /> con interactividad Neural Expressive:
 *
 *  · magnetic hover (cursor-reactive specular)
 *  · ripple on click (Material 3 Expressive feedback)
 *  · spring press scale (transición curva expressive verificada)
 *
 * No reemplaza al Button base — sólo se usa cuando quieres dar "vida" extra
 * a un CTA (hero primario, plan destacado, "Probar gratis"). Mantiene 100%
 * compatibilidad con la API de Button (variantes, sizes, asChild, etc).
 */
type ButtonProps = React.ComponentProps<typeof Button> & VariantProps<typeof buttonVariants>;
export const ExpressiveButton = forwardRef<HTMLButtonElement, ButtonProps>(function ExpressiveButton(t0, ref) {
  const $ = _c(17);
  let className;
  let onPointerDown;
  let onPointerMove;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      onPointerMove,
      onPointerDown,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onPointerDown;
    $[3] = onPointerMove;
    $[4] = rest;
  } else {
    className = $[1];
    onPointerDown = $[2];
    onPointerMove = $[3];
    rest = $[4];
  }
  let t1;
  if ($[5] !== onPointerMove) {
    t1 = event => {
      if (prefersReducedMotion()) {
        onPointerMove?.(event);
        return;
      }
      const el = event.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      el.style.setProperty("--mx", `${x}px`);
      el.style.setProperty("--my", `${y}px`);
      el.style.setProperty("--mx-pct", String(x / rect.width * 100));
      el.style.setProperty("--my-pct", String(y / rect.height * 100));
      onPointerMove?.(event);
    };
    $[5] = onPointerMove;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handlePointerMove = t1;
  let t2;
  if ($[7] !== onPointerDown) {
    t2 = event_0 => {
      if (prefersReducedMotion()) {
        onPointerDown?.(event_0);
        return;
      }
      const el_0 = event_0.currentTarget as HTMLElement;
      const rect_0 = el_0.getBoundingClientRect();
      el_0.style.setProperty("--ripple-x", `${event_0.clientX - rect_0.left}px`);
      el_0.style.setProperty("--ripple-y", `${event_0.clientY - rect_0.top}px`);
      onPointerDown?.(event_0);
    };
    $[7] = onPointerDown;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handlePointerDown = t2;
  let t3;
  if ($[9] !== className) {
    t3 = cn("magnetic ripple-surface", className);
    $[9] = className;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== handlePointerDown || $[12] !== handlePointerMove || $[13] !== ref || $[14] !== rest || $[15] !== t3) {
    t4 = <Button ref={ref} className={t3} onPointerMove={handlePointerMove} onPointerDown={handlePointerDown} {...rest} />;
    $[11] = handlePointerDown;
    $[12] = handlePointerMove;
    $[13] = ref;
    $[14] = rest;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});